import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface UserProfileProps {
  isCollapsed?: boolean;
}

const UserProfile: React.FC<UserProfileProps> = ({ isCollapsed = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isAdmin = user?.role?.toLowerCase?.() === 'admin';
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/auth');
    setIsMenuOpen(false);
  };

  // Get user's initial for avatar
  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : 
                     user?.username ? user.username.charAt(0).toUpperCase() : 'U';

  // Get user's organization
  const orgName = user?.organization_code || '';
  
  return (
    <div className="relative w-full" ref={menuRef}>
      {/* Avatar and user info */}
      <button 
        className={`flex items-center w-full ${isCollapsed ? 'justify-center' : ''}`}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-expanded={isMenuOpen}
        aria-haspopup="true"
      >
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden border-2 border-blue-600">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="User avatar" className="w-full h-full object-cover" />
          ) : (
            <span className="text-blue-700 font-semibold text-lg">{userInitial}</span>
          )}
        </div>
        
        {!isCollapsed && (
          <div className="ml-3 flex flex-col text-left">
            <span className="text-sm font-medium text-gray-900 truncate">
              {user?.name || user?.username || "User"}
            </span>
            <span className="text-xs text-gray-500 truncate">
              {orgName}
            </span>
          </div>
        )}
      </button>
      
      {/* Flyout menu */}
      {isMenuOpen && (
        <div className={`absolute ${isCollapsed ? 'left-12 top-0' : 'left-0 bottom-full mb-2'} w-56 bg-white rounded-lg shadow-lg py-2 z-50 border border-gray-200`}>
          {/* User info section at top of menu */}
          <div className="px-4 py-2 border-b border-gray-200">
            <div className="flex items-center mb-2">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden border-2 border-blue-600">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="User avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-blue-700 font-semibold text-lg">{userInitial}</span>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {user?.name || user?.username || "User"}
                </p>
                <p className="text-xs text-gray-500">
                  {orgName}
                </p>
              </div>
            </div>
            {/* Removed Profile settings text as requested */}
          </div>
          
          {/* Menu items */}
          <div className="py-1">
            <button
              onClick={() => {
                setIsMenuOpen(false);
                navigate('/v3/profile');
              }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-gray-500" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              My Profile
            </button>
            
            <button
              onClick={() => {
                setIsMenuOpen(false);
                navigate('/v3/settings');
              }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-gray-500" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </button>

            {/* Admin-only: Real-time Examples */}
            {isAdmin && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate('/v3/examples/realtime');
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              >
                <svg className="w-4 h-4 mr-3 text-gray-500" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Real-time Examples
              </button>
            )}

            <div className="border-t border-gray-200 my-1"></div>
            
            <button
              onClick={handleLogout}
              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-red-500" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
